import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import InternalOrderService from './InternalOrderService';
import axios from 'axios';
import { header } from './header.encry';
import { clearAuthData, getAuthData } from '../utils/authStorage';
import { PanelLeft, Plus, Bell, Clock3, Check, ArrowUpRight, MessageSquare, ChevronDown, UserRound, ChartNoAxesCombined, Settings, CircleHelp, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from './ui/dropdown-menu';

function Navbar(props) {
  const navigate = useNavigate();
  const [login, setLogin] = useState('');
  const [counts, setCounts] = useState({ pending: 0, accepted: 0 });
  const [globalMessage, setGlobalMessage] = useState('');

  const roles = JSON.parse(sessionStorage.getItem('roles') || '[]');
  const isDriver = roles.includes('driver');
  const isAdmin = roles.includes('admin');
  const isManager = roles.includes('manager');
  const canAccessInternalOrders = isAdmin || isManager;
  const allowed = canAccessInternalOrders;
  const totalNotifications = counts.pending + counts.accepted;

  const Logout = () => {
    sessionStorage.removeItem("token");
    sessionStorage.clear();
    clearAuthData();
    navigate("/login");
    alert("Déconnecté");
  };

  // Pour remplacer les icônes Feather après chaque render
  useEffect(() => {
    if (window.feather) {
      window.feather.replace();
    }
  });

  useEffect(() => {
    const resolveLogin = () => {
      const sessionName = sessionStorage.getItem('name');
      if (sessionName) {
        setLogin(sessionName);
        return;
      }

      const cachedAuth = getAuthData();
      if (cachedAuth?.name) {
        setLogin(cachedAuth.name);
      }
    };

    resolveLogin();

    const handleStorage = (event) => {
      if (event.key === 'auth') {
        resolveLogin();
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    // don't load internal order counts for drivers
    if (!allowed || isDriver) return;
    (async () => {
      try {
        const data = await InternalOrderService.fetchCounts();
        setCounts({
          pending: data.pending || 0,
          accepted: data.accepted || 0,
        });
      } catch (e) {
        console.error(e);
      }
    })();
  }, [allowed]);

  useEffect(() => {
    const loadGlobalMessage = async () => {
      try {
        const API_URL = process.env.REACT_APP_PEGASUS_API_URL;
        const res = await axios.get(`${API_URL}/variables`, header());
        const list = res?.data || [];
        const vars = Array.isArray(list) ? list : (list.response || list.data || []);
        const gm = (vars.find(v => String(v.name).toLowerCase() === 'globalmessage') || {});
        setGlobalMessage(gm.value || '');
      } catch (e) {
        // fallback stays
      }
    };
    loadGlobalMessage();
  }, []);

  const handleSidebarToggle = (event) => {
    event.preventDefault();

    window.dispatchEvent(new Event('pegasus:toggle-sidebar'));
  };

  return (
    <header className="pegasus-ui pegasus-header">
      <Button id="pegasus-menu-toggle" variant="ghost" size="icon" aria-label="Ouvrir ou réduire le menu" onClick={handleSidebarToggle}>
        <PanelLeft size={18} />
      </Button>
      <div className="pegasus-header-title">
        <strong>Espace de gestion</strong>
        <small title={globalMessage || 'Le Céladon · Pegasus'}>{globalMessage || 'Le Céladon · Pegasus'}</small>
      </div>
      <div className="pegasus-header-actions">
        {canAccessInternalOrders && <DropdownMenu>
          <DropdownMenuTrigger asChild><Button size="sm" aria-label="Ajouter"><Plus /><span className="pegasus-action-label">Ajouter</span></Button></DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Créer dans Pegasus</DropdownMenuLabel>
            <DropdownMenuItem asChild><Link to="/carrefourSell">Vente Carrefour</Link></DropdownMenuItem>
            <DropdownMenuItem asChild><Link to="/internal-orders">Commande Interne</Link></DropdownMenuItem>
            <DropdownMenuItem asChild><Link to="/storeQuickProduct">Produit</Link></DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>}
        {allowed && <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={`Notifications : ${totalNotifications}`} className="sc-relative">
              <Bell /><span className="pegasus-notification-dot">{totalNotifications}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="sc-w-72">
            <DropdownMenuLabel>{totalNotifications > 0 ? `${totalNotifications} notifications` : 'Notifications'}</DropdownMenuLabel>
            {counts.pending > 0 && <DropdownMenuItem asChild><Link to="/internal-orders"><Clock3 size={15} />{counts.pending} commande(s) en attente</Link></DropdownMenuItem>}
            {counts.accepted > 0 && <DropdownMenuItem asChild><Link to="/internal-orders"><Check size={15} />{counts.accepted} commande(s) acceptée(s)</Link></DropdownMenuItem>}
            {totalNotifications === 0 && <div className="sc-px-3 sc-py-3 sc-text-xs sc-text-muted-foreground">Aucune commande interne</div>}
            <DropdownMenuSeparator /><DropdownMenuItem asChild><Link to="/internal-orders">Voir les commandes<ArrowUpRight size={14} /></Link></DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>}
        <DropdownMenu>
          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" aria-label="Messages"><MessageSquare /></Button></DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="sc-w-72">
            <DropdownMenuLabel>Messages</DropdownMenuLabel>
            {[
              ['Vanessa Tucker', 'Nam pretium turpis et arcu. Duis arcu tortor.', '15m ago'],
              ['William Harris', 'Curabitur ligula sapien euismod vitae.', '2h ago'],
              ['Christina Mason', 'Pellentesque auctor neque nec urna.', '4h ago'],
              ['Sharon Lessman', 'Aenean tellus metus, bibendum sed, posuere ac, mattis non.', '5h ago'],
            ].map(([name, text, date]) => <DropdownMenuItem key={name} asChild><a href="#">
              <span><strong className="sc-block sc-text-xs">{name}</strong><span className="sc-block sc-text-xs sc-text-muted-foreground">{text}</span><small>{date}</small></span>
            </a></DropdownMenuItem>)}
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="pegasus-user-trigger" aria-label={`Compte de ${login || 'mon compte'}`}>
              <span className="pegasus-user-avatar" aria-hidden="true">{(login || 'LC').slice(0, 2).toUpperCase()}</span>
              <span className="pegasus-user-name">{login || 'Mon compte'}</span><ChevronDown size={13} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{login || 'Mon compte'}</DropdownMenuLabel>
            <DropdownMenuItem asChild><a href="pages-profile.html"><UserRound size={15} />Profile</a></DropdownMenuItem>
            <DropdownMenuItem asChild><a href="#"><ChartNoAxesCombined size={15} />Analytics</a></DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild><a href="index.html"><Settings size={15} />Settings &amp; Privacy</a></DropdownMenuItem>
            <DropdownMenuItem asChild><a href="#"><CircleHelp size={15} />Help Center</a></DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={Logout}><LogOut size={15} />Se déconnecter</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

export default Navbar;
